/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Shape and formatting for irrigation schedules.
 * MIRRORS backend/utils/waterSchedule.js — keep the two in step.
 *
 * One entry is a water source plus when it is applied. The day pattern is two
 * weeks of seven booleans (index 0 = Monday) rather than one, because an
 * alternate-day pattern is ambiguous until you can see the second week.
 *
 * The same shape is used by the client's request form, the schedule records
 * stored against the project, and the admin's editor, so a requested schedule
 * can be applied without translation.
 */

export const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const emptyWeek = () => [false, false, false, false, false, false, false];

export const normalizeWeek = (week) => {
  const out = emptyWeek();
  if (!Array.isArray(week)) return out;
  for (let i = 0; i < 7; i += 1) out[i] = Boolean(week[i]);
  return out;
};

const toCount = (value, max) => {
  if (value === '' || value === null || value === undefined) return 0;
  const n = Math.floor(Number(value));
  if (Number.isNaN(n) || n < 0) return 0;
  return max === undefined ? n : Math.min(n, max);
};

export const normalizeDuration = (duration) => {
  const d = duration || {};
  return {
    hours: toCount(d.hours, 23),
    minutes: toCount(d.minutes, 59),
    seconds: toCount(d.seconds, 59)
  };
};

export const durationToSeconds = (duration) => {
  const d = normalizeDuration(duration);
  return (d.hours * 3600) + (d.minutes * 60) + d.seconds;
};

/** A blank entry for a given water source. */
export const emptyScheduleEntry = (water = null, waterLabel = '') => ({
  water: water || null,
  water_label: waterLabel,
  week1: emptyWeek(),
  week2: emptyWeek(),
  times_per_day: '',
  duration: { hours: '', minutes: '', seconds: '' },
  comments: ''
});

export const isEmptyScheduleEntry = (entry) => {
  if (!entry) return true;
  return !entry.water
    && !(entry.water_label || '').trim()
    && !normalizeWeek(entry.week1).some(Boolean)
    && !normalizeWeek(entry.week2).some(Boolean)
    && !toCount(entry.times_per_day)
    && durationToSeconds(entry.duration) === 0
    && !(entry.comments || '').trim();
};

/** "Mon, Wed, Fri (every week)" / "Week 1: … · Week 2: …" / "No days selected". */
export const describeDays = (week1, week2) => {
  const names = (w) => normalizeWeek(w).map((on, i) => (on ? DAY_SHORT[i] : null)).filter(Boolean);
  const n1 = names(week1);
  const n2 = names(week2);
  if (n1.length === 0 && n2.length === 0) return 'No days selected';
  if (n1.join() === n2.join()) return `${n1.join(', ')} (every week)`;
  return `Week 1: ${n1.length ? n1.join(', ') : 'none'} · Week 2: ${n2.length ? n2.join(', ') : 'none'}`;
};

/** "2 h 38 min", "15 sec", "—". */
export const formatDuration = (duration) => {
  const d = normalizeDuration(duration);
  const parts = [];
  if (d.hours) parts.push(`${d.hours} h`);
  if (d.minutes) parts.push(`${d.minutes} min`);
  if (d.seconds) parts.push(`${d.seconds} sec`);
  return parts.length ? parts.join(' ') : '—';
};

export const summarizeScheduleEntry = (entry) => {
  if (!entry) return '';
  const bits = [entry.water_label || entry.water?.label || 'Water'];
  bits.push(describeDays(entry.week1, entry.week2));
  const times = toCount(entry.times_per_day);
  if (times) bits.push(`${times}×/day`);
  const dur = formatDuration(entry.duration);
  if (dur !== '—') bits.push(dur);
  return bits.join(' · ');
};

/**
 * In effect = it has started and has not ended. A schedule dated to start
 * later is upcoming, not current: showing it as in effect would tell an admin
 * to water on a pattern that is not live yet.
 */
export const isScheduleCurrent = (schedule, on = new Date()) => {
  if (!schedule || schedule.is_deleted) return false;
  if (schedule.start_date && new Date(schedule.start_date) > on) return false;
  if (!schedule.end_date) return true;
  return new Date(schedule.end_date) >= on;
};

export const isScheduleUpcoming = (schedule, on = new Date()) => {
  if (!schedule || schedule.is_deleted) return false;
  if (schedule.end_date && new Date(schedule.end_date) < on) return false;
  return Boolean(schedule.start_date) && new Date(schedule.start_date) > on;
};

export const formatScheduleRange = (schedule) => {
  const start = schedule?.start_date ? new Date(schedule.start_date).toLocaleDateString() : '—';
  const end = schedule?.end_date ? new Date(schedule.end_date).toLocaleDateString() : 'ongoing';
  return `${start} → ${end}`;
};

const waterScheduleUtils = {
  DAY_LABELS,
  DAY_SHORT,
  emptyWeek,
  normalizeWeek,
  normalizeDuration,
  durationToSeconds,
  emptyScheduleEntry,
  isEmptyScheduleEntry,
  describeDays,
  formatDuration,
  summarizeScheduleEntry,
  isScheduleCurrent,
  isScheduleUpcoming,
  formatScheduleRange
};

export default waterScheduleUtils;
